import React, { useCallback, useEffect, useRef, useState } from "react";
import { CSSProperties, FC } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import CloseIcon from "../../customIcons/Close";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { Breakpoint } from "../../enums/Breakpoints";

const ANIMATION_DURATION = 500;

interface Props {
  header?: JSX.Element | string;
  hiddenHeader?: boolean;
  id?: string;
  isOpened: boolean;
  onClose: () => void;
  containerStyle?: CSSProperties;
  style?: CSSProperties;
  className?: string;
}

const Modal: FC<Props> = ({
  children,
  header,
  hiddenHeader,
  id,
  isOpened,
  onClose,
  containerStyle,
  style,
  className,
}) => {
  // delay for closing animation
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    let timeout: any = null;
    if (isOpened) {
      setIsVisible(true);
    } else {
      timeout = setTimeout(() => setIsVisible(false), ANIMATION_DURATION);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isOpened, setIsVisible]);

  // close on escape
  const closeOnEsc = useCallback(
    ({ key }: KeyboardEvent) => {
      if (key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );
  useEffect(() => {
    if (isOpened) {
      document.addEventListener("keydown", closeOnEsc);
    }
    return () => {
      document.removeEventListener("keydown", closeOnEsc);
    };
  }, [isOpened, closeOnEsc]);

  // close on outside click
  const modalRef = useRef<HTMLDivElement>(null);
  useOutsideClick(modalRef, onClose);

  return createPortal(
    <>
      {isVisible && (
        <Container id={id} className={className} style={containerStyle}>
          <ModalElement isVisible={isOpened} ref={modalRef} style={style}>
            {!hiddenHeader && (
              <Header>
                <HeaderContent>
                  {header && typeof header === "string" ? (
                    <HeaderTitle>{header}</HeaderTitle>
                  ) : (
                    header
                  )}
                </HeaderContent>
                <CloseContainer onClick={onClose}>
                  <CloseIcon />
                </CloseContainer>
              </Header>
            )}
            <div>{children}</div>
          </ModalElement>
          <Backdrop />
        </Container>
      )}
    </>,
    document.querySelector("body")!
  );
};

const CloseContainer = styled.div`
  display: flex;
  cursor: pointer;
  padding-top: 1px;
`;

const Backdrop = styled.div`
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  left: 0;
  bottom: 0;
  top: 0;
  right: 0;
`;

const Container = styled.div`
  bottom: 0;
  left: 0;
  overflow: auto;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 3;
  font-family: ${({ theme }) => theme.redesign.regularFont};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalElement = styled.div<{ isVisible: boolean }>`
  position: relative;
  animation: ${({ isVisible }) => (isVisible ? `fadeIn` : `fadeOut`)}
    ${ANIMATION_DURATION}ms 1;
  background: ${({ theme }) => theme.redesign.lightBackground};
  display: flex;
  flex-direction: column;
  padding: 20px;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  z-index: 3;

  @keyframes fadeIn {
    0% {
      opacity: 0;
      transform: translateY(-30px);
    }

    100% {
      opacity: 1;
      transform: translateY(0%);
    }
  }

  @keyframes fadeOut {
    0% {
      opacity: 1;
      transform: translateY(0%);
    }

    100% {
      opacity: 0;
      transform: translateY(-30px);
    }
  }

  @media ${Breakpoint.MinTablet} {
    padding: 30px;
    width: 550px;
    height: fit-content;
  }
`;

const Header = styled.div`
  padding-bottom: 5px;
  display: flex;
  justify-content: space-between;
`;

const HeaderTitle = styled.h2`
  font-family: ${({ theme }) => theme.redesign.heavyFont};
  font-size: 22px;
  line-height: 22px;
  margin: 0;

  @media ${Breakpoint.MinTablet} {
    font-size: 32px;
    line-height: 32px;
  }
`;

const HeaderContent = styled.div`
  width: calc(100% - 40px);
`;

export default Modal;
